import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter, MoreHorizontal, Mail, Phone, MapPin, Edit, Trash2, UserPlus, Building2, Eye, FileText, CreditCard, Heart, User, Shield, Calendar, DollarSign, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { useUsers } from '../../../hooks/useDatabase';
import { formatDate, getYearsOfService } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import { STATE_NAMES } from '../../../lib/usPayrollData';
import { generateW4PDF } from '../../../lib/export';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Label } from '../../ui/label';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

export default function EmployeesPage() {
  const { user: currentUser } = useAuthStore();
  const { confirm } = useConfirmDialog();
  const { addUser, updateUser, loading } = useUsers();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    firstName: '', lastName: '', email: '', department: '', position: '', role: 'employee', supervisorId: '', location: '', phone: '', dateOfBirth: '', hireDate: '', gender: '', workState: '', residentState: ''
  });
  
  // Employee details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState({
    w4: null,
    bankAccounts: [],
    compensation: null,
    emergency: null
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    const data = await db.users.toArray();
    // Deduplicate by email to avoid double-seeded users
    const seenEmails = new Set();
    const cleaned = data.filter(u => {
      if (!u.email) return false;
      const key = u.email.toLowerCase();
      if (seenEmails.has(key)) return false;
      seenEmails.add(key);
      return true;
    });
    setUsers(cleaned.filter(u => u.role !== 'admin' && u.status !== 'deleted'));
  };

  // Load employee details for the details modal
  const loadEmployeeDetails = async (employee) => {
    try {
      const [w4, bankAccounts, compensation] = await Promise.all([
        db.employeeW4.where('userId').equals(employee.id).first(),
        db.employeeBankAccounts.where('userId').equals(employee.id).toArray(),
        db.employeeCompensation.where('userId').equals(employee.id).first()
      ]);
      
      // Parse emergency contacts from the array format saved by onboarding
      const emergencyContacts = employee.profile?.emergencyContacts || [];
      const primaryContact = emergencyContacts.find(c => c.isPrimary) || emergencyContacts[0];
      const secondaryContact = emergencyContacts.find(c => !c.isPrimary) || emergencyContacts[1];
      
      const emergencyData = primaryContact ? {
        primaryName: primaryContact.name,
        primaryRelationship: primaryContact.relationship,
        primaryPhone: primaryContact.phone,
        primaryEmail: primaryContact.email,
        secondaryName: secondaryContact?.name,
        secondaryRelationship: secondaryContact?.relationship,
        secondaryPhone: secondaryContact?.phone,
        secondaryEmail: secondaryContact?.email
      } : (employee.profile?.emergencyContact || employee.profile?.emergency || null);
      
      setEmployeeDetails({
        w4: w4 || null,
        bankAccounts: bankAccounts || [],
        compensation: compensation || null,
        emergency: emergencyData
      });
      setSelectedEmployee(employee);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error loading employee details:', error);
      toast.error('Failed to load employee details');
    }
  };

  const employees = users;
  const supervisors = users.filter(u => u.role === 'supervisor' && u.status !== 'deleted');
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = searchQuery === '' ||
      emp.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = departmentFilter === 'all' || emp.profile?.department === departmentFilter;
    const matchesRole = roleFilter === 'all' || emp.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
    return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
  });

  const departments = [...new Set(employees.map(e => e.profile?.department).filter(Boolean))];

  const handleSaveEmployee = async () => {
    if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.email || !newEmployee.department || !newEmployee.position) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      const supervisorId = newEmployee.role === 'employee'
        ? (newEmployee.supervisorId || (supervisors[0]?.id || null))
        : null;

      if (newEmployee.role === 'employee' && !supervisorId && supervisors.length > 0) {
        toast.error('Please select a supervisor');
        return;
      }
      const hireDateMs = newEmployee.hireDate ? new Date(newEmployee.hireDate).getTime() : Date.now();
      const dobMs = newEmployee.dateOfBirth ? new Date(newEmployee.dateOfBirth).getTime() : undefined;
      if (editingUserId) {
        await updateUser(editingUserId, {
          email: newEmployee.email,
          role: newEmployee.role,
          supervisorId,
          profile: {
            ...users.find(u => u.id === editingUserId)?.profile,
            firstName: newEmployee.firstName,
            lastName: newEmployee.lastName,
            department: newEmployee.department,
            position: newEmployee.position,
            location: newEmployee.location,
            phone: newEmployee.phone,
            dateOfBirth: dobMs ?? users.find(u => u.id === editingUserId)?.profile?.dateOfBirth,
            hireDate: hireDateMs,
            gender: newEmployee.gender,
            workState: newEmployee.workState,
            residentState: newEmployee.residentState
          }
        });
        toast.success('Employee updated successfully!');
      } else {
        const password = newEmployee.role === 'supervisor' ? 'Super@123' : 'Employee@123';
        await addUser({
          ...newEmployee,
          adminId: currentUser.adminId || currentUser.id,
          supervisorId,
          password,
          dateOfBirth: dobMs,
          hireDate: hireDateMs,
          gender: newEmployee.gender,
          workState: newEmployee.workState,
          residentState: newEmployee.residentState
        });
        toast.success('Employee added successfully!');
      }
      setShowAddModal(false);
      setEditingUserId(null);
      setNewEmployee({ firstName: '', lastName: '', email: '', department: '', position: '', role: 'employee', supervisorId: '', location: '', phone: '', dateOfBirth: '', hireDate: '', gender: '', workState: '', residentState: '' });
      loadUsers();
    } catch (error) {
      toast.error('Failed to save employee');
    }
  };

  const openEditEmployee = (employee) => {
    setEditingUserId(employee.id);
    setNewEmployee({
      firstName: employee.profile?.firstName || '',
      lastName: employee.profile?.lastName || '',
      email: employee.email || '',
      department: employee.profile?.department || '',
      position: employee.profile?.position || '',
      role: employee.role || 'employee',
      supervisorId: employee.supervisorId || '',
      location: employee.profile?.location || '',
      phone: employee.profile?.phone || '',
      dateOfBirth: employee.profile?.dateOfBirth ? new Date(employee.profile.dateOfBirth).toISOString().slice(0, 10) : '',
      hireDate: employee.profile?.hireDate ? new Date(employee.profile.hireDate).toISOString().slice(0, 10) : '',
      gender: employee.profile?.gender || '',
      workState: employee.profile?.workState || '',
      residentState: employee.profile?.residentState || ''
    });
    setShowAddModal(true);
  };
  const handleDeleteEmployee = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Employee',
      description: 'Are you sure you want to delete this employee?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (confirmed) {
      await db.users.update(id, { status: 'deleted' });
      toast.success('Employee deleted');
      loadUsers();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-500 mt-1">Manage your organization's employees</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
          <UserPlus className="w-4 h-4 mr-2" /> Add Employee
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search by name or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEmployees.map(employee => (
          <Card key={employee.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {employee.profile?.firstName?.[0]}{employee.profile?.lastName?.[0]}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{employee.profile?.firstName} {employee.profile?.lastName}</h3>
                    <p className="text-sm text-gray-500">{employee.profile?.position}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => loadEmployeeDetails(employee)}><Eye className="w-4 h-4 mr-2" /> View Details</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEditEmployee(employee)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem><Mail className="w-4 h-4 mr-2" /> Send Email</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-rose-600" onClick={() => handleDeleteEmployee(employee.id)}>
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600"><Mail className="w-4 h-4 text-gray-400" />{employee.email}</div>
                <div className="flex items-center gap-2 text-sm text-gray-600"><Phone className="w-4 h-4 text-gray-400" />{employee.profile?.phone || 'N/A'}</div>
                <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin className="w-4 h-4 text-gray-400" />{employee.profile?.location || 'N/A'}</div>
                {employee.profile?.workState && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span>Work State: <span className="font-medium text-teal-600">{employee.profile.workState}</span></span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <Badge className={employee.role === 'supervisor' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'}>{employee.role}</Badge>
                  <Badge className={employee.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>{employee.status}</Badge>
                </div>
                <span className="text-xs text-gray-400">{getYearsOfService(employee.profile?.hireDate)}y tenure</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEmployees.length === 0 && (<div className="text-center py-12"><p className="text-gray-500">No employees found</p></div>)}

      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) { setEditingUserId(null); setNewEmployee({ firstName: '', lastName: '', email: '', department: '', position: '', role: 'employee', supervisorId: '', location: '', phone: '', dateOfBirth: '', hireDate: '', gender: '', workState: '', residentState: '' }); } }}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUserId ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {editingUserId ? 'Update employee information' : 'Add a new employee to your organization. Work state is required for US tax calculations.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input placeholder="John" value={newEmployee.firstName} onChange={(e) => setNewEmployee({...newEmployee, firstName: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input placeholder="Doe" value={newEmployee.lastName} onChange={(e) => setNewEmployee({...newEmployee, lastName: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" placeholder="john.doe@company.com" value={newEmployee.email} onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={newEmployee.gender} onValueChange={(v) => setNewEmployee({...newEmployee, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select value={newEmployee.department} onValueChange={(v) => setNewEmployee({...newEmployee, department: v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={newEmployee.role} onValueChange={(v) => setNewEmployee({...newEmployee, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newEmployee.role === 'employee' && (
              <div className="space-y-2">
                <Label>Supervisor</Label>
                <Select
                  value={newEmployee.supervisorId || ''}
                  onValueChange={(v) => setNewEmployee({...newEmployee, supervisorId: v})}
                >
                  <SelectTrigger><SelectValue placeholder={supervisors.length ? 'Select supervisor' : 'No supervisors available'} /></SelectTrigger>
                  <SelectContent>
                    {supervisors.length === 0 && <SelectItem value="">No supervisors available</SelectItem>}
                    {supervisors.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.profile?.firstName} {s.profile?.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Position *</Label>
              <Input placeholder="Software Developer" value={newEmployee.position} onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input placeholder="HQ" value={newEmployee.location} onChange={(e) => setNewEmployee({...newEmployee, location: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input placeholder="555-0100" value={newEmployee.phone} onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date of Birth (optional)</Label>
                <Input type="date" value={newEmployee.dateOfBirth} onChange={(e) => setNewEmployee({...newEmployee, dateOfBirth: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Date of Joining</Label>
                <Input type="date" value={newEmployee.hireDate} onChange={(e) => setNewEmployee({...newEmployee, hireDate: e.target.value})} />
                <p className="text-xs text-gray-500">Defaults to today if left blank.</p>
              </div>
            </div>
            
            {/* US Tax State Selection */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-teal-600" />
                <span className="font-medium text-gray-700">US Tax Information</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Required for US payroll tax calculations. Different states have different tax rates.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Work State *</Label>
                  <Select value={newEmployee.workState} onValueChange={(v) => setNewEmployee({...newEmployee, workState: v, residentState: newEmployee.residentState || v})}>
                    <SelectTrigger><SelectValue placeholder="Select work state..." /></SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-[200px]">
                        {Object.entries(STATE_NAMES).map(([code, name]) => (
                          <SelectItem key={code} value={code}>
                            {code} - {name}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">State where employee works</p>
                </div>
                <div className="space-y-2">
                  <Label>Resident State</Label>
                  <Select value={newEmployee.residentState} onValueChange={(v) => setNewEmployee({...newEmployee, residentState: v})}>
                    <SelectTrigger><SelectValue placeholder="Same as work state" /></SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-[200px]">
                        {Object.entries(STATE_NAMES).map(([code, name]) => (
                          <SelectItem key={code} value={code}>
                            {code} - {name}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">State where employee lives (if different)</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEmployee} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">
              {loading ? 'Saving...' : editingUserId ? 'Save Changes' : 'Add Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold">
                {selectedEmployee?.profile?.firstName?.[0]}{selectedEmployee?.profile?.lastName?.[0]}
              </div>
              <div>
                <span>{selectedEmployee?.profile?.firstName} {selectedEmployee?.profile?.lastName}</span>
                <p className="text-sm font-normal text-gray-500">{selectedEmployee?.profile?.position} • {selectedEmployee?.profile?.department}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="personal" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="personal" className="gap-1"><User className="w-4 h-4" /> Personal</TabsTrigger>
              <TabsTrigger value="tax" className="gap-1"><FileText className="w-4 h-4" /> W-4 Tax</TabsTrigger>
              <TabsTrigger value="banking" className="gap-1"><CreditCard className="w-4 h-4" /> Banking</TabsTrigger>
              <TabsTrigger value="emergency" className="gap-1"><Heart className="w-4 h-4" /> Emergency</TabsTrigger>
            </TabsList>

            {/* Personal Information Tab */}
            <TabsContent value="personal" className="space-y-4 mt-4">
              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4 text-teal-600" />
                    Personal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Full Name</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.firstName} {selectedEmployee?.profile?.middleName || ''} {selectedEmployee?.profile?.lastName}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Preferred Name</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.preferredName || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium">{selectedEmployee?.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Date of Birth</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.dateOfBirth ? new Date(selectedEmployee.profile.dateOfBirth).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Gender</Label>
                    <p className="font-medium capitalize">{selectedEmployee?.profile?.gender || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Marital Status</Label>
                    <p className="font-medium capitalize">{selectedEmployee?.profile?.maritalStatus || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">SSN (Last 4)</Label>
                    <p className="font-medium">***-**-{selectedEmployee?.profile?.ssnLast4 || '****'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-teal-600" />
                    Employment Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Position</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.position || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Department</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.department || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Hire Date</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.hireDate ? new Date(selectedEmployee.profile.hireDate).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Tenure</Label>
                    <p className="font-medium">{getYearsOfService(selectedEmployee?.profile?.hireDate)} years</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Work Location</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.location || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Work State</Label>
                    <p className="font-medium">{selectedEmployee?.profile?.workState ? `${selectedEmployee.profile.workState} - ${STATE_NAMES[selectedEmployee.profile.workState] || ''}` : 'N/A'}</p>
                  </div>
                  {employeeDetails.compensation && (
                    <>
                      <div>
                        <Label className="text-gray-500">Pay Type</Label>
                        <p className="font-medium capitalize">{employeeDetails.compensation.payType || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Pay Rate</Label>
                        <p className="font-medium">
                          ${employeeDetails.compensation.payRate?.toLocaleString() || '0'}
                          {employeeDetails.compensation.payType === 'hourly' ? '/hr' : '/yr'}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-teal-600" />
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div className="col-span-2">
                    <Label className="text-gray-500">Mailing Address</Label>
                    <p className="font-medium">
                      {selectedEmployee?.profile?.address?.mailing?.address1 || selectedEmployee?.profile?.address?.street || selectedEmployee?.profile?.mailingAddress1 || 'N/A'}
                      {(selectedEmployee?.profile?.address?.mailing?.address2 || selectedEmployee?.profile?.mailingAddress2) && 
                        `, ${selectedEmployee?.profile?.address?.mailing?.address2 || selectedEmployee?.profile?.mailingAddress2}`}
                      {(selectedEmployee?.profile?.address?.mailing?.city || selectedEmployee?.profile?.address?.city || selectedEmployee?.profile?.mailingCity) && 
                        `, ${selectedEmployee?.profile?.address?.mailing?.city || selectedEmployee?.profile?.address?.city || selectedEmployee?.profile?.mailingCity}`}
                      {(selectedEmployee?.profile?.address?.mailing?.state || selectedEmployee?.profile?.address?.state || selectedEmployee?.profile?.mailingState) && 
                        `, ${selectedEmployee?.profile?.address?.mailing?.state || selectedEmployee?.profile?.address?.state || selectedEmployee?.profile?.mailingState}`}
                      {(selectedEmployee?.profile?.address?.mailing?.zip || selectedEmployee?.profile?.address?.zip || selectedEmployee?.profile?.mailingZip) && 
                        ` ${selectedEmployee?.profile?.address?.mailing?.zip || selectedEmployee?.profile?.address?.zip || selectedEmployee?.profile?.mailingZip}`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* W-4 Tax Information Tab */}
            <TabsContent value="tax" className="space-y-4 mt-4">
              <Card className="border shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-600" />
                      Form W-4 Information
                    </CardTitle>
                    <CardDescription>Federal tax withholding elections</CardDescription>
                  </div>
                  {employeeDetails.w4 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          try {
                            generateW4PDF(employeeDetails.w4, selectedEmployee);
                            toast.success('W-4 form downloaded successfully');
                          } catch (error) {
                            console.error('Error generating W-4:', error);
                            toast.error('Failed to generate W-4 form');
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export W-4
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open('/fw4.pdf', '_blank')}
                        title="View blank IRS Form W-4"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Blank Form
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {employeeDetails.w4 ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500">Filing Status</Label>
                        <p className="font-medium capitalize">{employeeDetails.w4.filingStatus || 'Single'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Multiple Jobs</Label>
                        <p className="font-medium">{employeeDetails.w4.multipleJobs ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Qualifying Children (Under 17)</Label>
                        <p className="font-medium">{employeeDetails.w4.qualifyingChildren || 0}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Other Dependents</Label>
                        <p className="font-medium">{employeeDetails.w4.otherDependents || 0}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Dependent Credit (Step 3)</Label>
                        <p className="font-medium text-emerald-600">${employeeDetails.w4.dependentCredit?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Other Income (4a)</Label>
                        <p className="font-medium">${employeeDetails.w4.otherIncome?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Deductions (4b)</Label>
                        <p className="font-medium">${employeeDetails.w4.deductions?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Extra Withholding (4c)</Label>
                        <p className="font-medium">${employeeDetails.w4.additionalWithholding?.toLocaleString() || 0}/pay period</p>
                      </div>
                      <Separator className="col-span-2 my-2" />
                      <div>
                        <Label className="text-gray-500">Exempt from Federal</Label>
                        <p className="font-medium">{employeeDetails.w4.exemptFromFederal ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Last Updated</Label>
                        <p className="font-medium">{employeeDetails.w4.updatedAt ? new Date(employeeDetails.w4.updatedAt).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No W-4 on file</p>
                      <p className="text-sm">Employee has not completed W-4 form</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-teal-600" />
                    State Tax Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-gray-500">Work State</Label>
                    <p className="font-medium">{employeeDetails.w4?.workState || selectedEmployee?.profile?.workState || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Resident State</Label>
                    <p className="font-medium">{employeeDetails.w4?.residentState || selectedEmployee?.profile?.residentState || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">State Allowances</Label>
                    <p className="font-medium">{employeeDetails.w4?.stateAllowances || 0}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Additional State Withholding</Label>
                    <p className="font-medium">${employeeDetails.w4?.additionalStateWithholding || 0}/pay period</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Banking Tab */}
            <TabsContent value="banking" className="space-y-4 mt-4">
              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-teal-600" />
                    Direct Deposit Accounts
                  </CardTitle>
                  <CardDescription>Bank accounts for payroll deposits</CardDescription>
                </CardHeader>
                <CardContent>
                  {employeeDetails.bankAccounts && employeeDetails.bankAccounts.length > 0 ? (
                    <div className="space-y-4">
                      {employeeDetails.bankAccounts.map((account, idx) => (
                        <div key={idx} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className={account.priority === 1 ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-700'}>
                              {account.priority === 1 ? 'Primary' : 'Secondary'}
                            </Badge>
                            <Badge variant="outline" className="capitalize">{account.accountType}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <Label className="text-gray-500">Bank Name</Label>
                              <p className="font-medium">{account.bankName || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Account Type</Label>
                              <p className="font-medium capitalize">{account.accountType}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Routing Number</Label>
                              <p className="font-medium font-mono">****{account.routingNumber?.slice(-4) || '****'}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Account Number</Label>
                              <p className="font-medium font-mono">****{account.accountNumber?.slice(-4) || '****'}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Deposit Type</Label>
                              <p className="font-medium capitalize">{account.depositType || 'Full'}</p>
                            </div>
                            {account.depositType === 'partial' && (
                              <div>
                                <Label className="text-gray-500">Deposit Amount</Label>
                                <p className="font-medium">${account.depositAmount?.toLocaleString() || 0}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No bank accounts on file</p>
                      <p className="text-sm">Employee will receive paper checks</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Emergency Contacts Tab */}
            <TabsContent value="emergency" className="space-y-4 mt-4">
              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-500" />
                    Emergency Contacts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(selectedEmployee?.profile?.emergencyContact?.primaryName || employeeDetails.emergency?.primaryName) ? (
                    <div className="space-y-4">
                      {/* Primary Contact */}
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="bg-red-100 text-red-700">Primary</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <Label className="text-gray-500">Name</Label>
                            <p className="font-medium">{selectedEmployee?.profile?.emergencyContact?.primaryName || employeeDetails.emergency?.primaryName || 'N/A'}</p>
                          </div>
                          <div>
                            <Label className="text-gray-500">Relationship</Label>
                            <p className="font-medium capitalize">{selectedEmployee?.profile?.emergencyContact?.primaryRelationship || employeeDetails.emergency?.primaryRelationship || 'N/A'}</p>
                          </div>
                          <div>
                            <Label className="text-gray-500">Phone</Label>
                            <p className="font-medium">{selectedEmployee?.profile?.emergencyContact?.primaryPhone || employeeDetails.emergency?.primaryPhone || 'N/A'}</p>
                          </div>
                          <div>
                            <Label className="text-gray-500">Email</Label>
                            <p className="font-medium">{selectedEmployee?.profile?.emergencyContact?.primaryEmail || employeeDetails.emergency?.primaryEmail || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Secondary Contact */}
                      {(selectedEmployee?.profile?.emergencyContact?.secondaryName || employeeDetails.emergency?.secondaryName) && (
                        <div className="p-4 border rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge variant="outline">Secondary</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <Label className="text-gray-500">Name</Label>
                              <p className="font-medium">{selectedEmployee?.profile?.emergencyContact?.secondaryName || employeeDetails.emergency?.secondaryName}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Relationship</Label>
                              <p className="font-medium capitalize">{selectedEmployee?.profile?.emergencyContact?.secondaryRelationship || employeeDetails.emergency?.secondaryRelationship}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Phone</Label>
                              <p className="font-medium">{selectedEmployee?.profile?.emergencyContact?.secondaryPhone || employeeDetails.emergency?.secondaryPhone}</p>
                            </div>
                            <div>
                              <Label className="text-gray-500">Email</Label>
                              <p className="font-medium">{selectedEmployee?.profile?.emergencyContact?.secondaryEmail || employeeDetails.emergency?.secondaryEmail}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Heart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No emergency contacts on file</p>
                      <p className="text-sm">Employee has not added emergency contact information</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>Close</Button>
            <Button onClick={() => {
              setShowDetailsModal(false);
              openEditEmployee(selectedEmployee);
            }} className="bg-teal-600 hover:bg-teal-700">
              <Edit className="w-4 h-4 mr-2" /> Edit Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
